/**
 * Enkel analyse av kamerabildene, gjort i nettleseren (ingen tjeneste, ingen kostnad).
 *
 *  - Lysnivå: snittlysstyrken i bildet. Bilder tatt mens vekstlyset er av, blir
 *    nesten svarte, og de hoppes over i timelapsen og i vekstkurven.
 *  - Grønt i bildet: andelen piksler der grønt dominerer, målt med «excess green»
 *    (ExG = 2g − r − b på normaliserte farger), et vanlig mål på bladdekke i
 *    plantefotografering. Det er et ANSLAG: vinkel, lys og jord påvirker tallet,
 *    men utviklingen over tid viser om plantene vokser.
 *
 * Bildet skaleres ned (f.eks. 64 × 48) før pikslene sendes hit.
 */

export interface BildeMaal {
  /** Snittlysstyrke 0–255. */
  lys: number;
  /** Andel grønne piksler, 0–1. */
  gront: number;
}

/** Under dette er bildet for mørkt til å si noe (vekstlyset var av). */
export const MORK_GRENSE = 30;
/**
 * ExG over denne regnes som blad. I tillegg må grønt slå rødt og blått tydelig:
 * kalibrert mot kassas egne bilder 23.09, der 0,08 alene talte gråbrune skygger
 * (41, 42, 32) og den blågrønne skjærematta som blad, og ga 18 % grønt i et bilde
 * uten planter. Nå gir de samme bildene 0–1 %, mens bladfarger under barene
 * (f.eks. 120, 150, 80) fortsatt teller.
 */
const EXG_GRENSE = 0.12;
const GRONT_OVER_ROED = 1.08;
const GRONT_OVER_BLAATT = 1.15;
/** For mørke piksler har tilfeldige farger — de telles ikke som blad. */
const MIN_PIKSEL_LYS = 35;

/** Mål et bilde fra RGBA-pikslene (4 tall per piksel, som ImageData.data). */
export function maalPiksler(rgba: ArrayLike<number>): BildeMaal {
  const n = Math.floor(rgba.length / 4);
  if (n === 0) return { lys: 0, gront: 0 };
  let sumLys = 0;
  let gronne = 0;
  for (let i = 0; i < n; i++) {
    const r = rgba[i * 4]!;
    const g = rgba[i * 4 + 1]!;
    const b = rgba[i * 4 + 2]!;
    const lys = 0.299 * r + 0.587 * g + 0.114 * b;
    sumLys += lys;
    const sum = r + g + b;
    if (sum > 0 && lys >= MIN_PIKSEL_LYS) {
      const exg = (2 * g - r - b) / sum;
      if (exg > EXG_GRENSE && g > r * GRONT_OVER_ROED && g > b * GRONT_OVER_BLAATT) gronne++;
    }
  }
  return { lys: sumLys / n, gront: gronne / n };
}

export function erMorkt(m: BildeMaal): boolean {
  return m.lys < MORK_GRENSE;
}

export interface VekstPunkt {
  dato: Date;
  gront: number;
}

/** Punkter i SVG-koordinater med fast 0–100 %-skala (y=36 er 0, y=4 er 100). */
export function vekstKurvePunkter(serie: VekstPunkt[]): { x: number; y: number }[] {
  if (serie.length === 0) return [];
  const start = serie[0]!.dato.getTime();
  const slutt = serie[serie.length - 1]!.dato.getTime();
  return serie.map((punkt, indeks) => {
    const tid = punkt.dato.getTime();
    const x = slutt > start ? ((tid - start) / (slutt - start)) * 300 : (indeks / Math.max(1, serie.length - 1)) * 300;
    const prosent = Math.max(0, Math.min(1, punkt.gront));
    return { x, y: 36 - prosent * 32 };
  });
}

/**
 * Endring i grønt fra første til siste punkt, i prosentpoeng.
 * null når det er for få punkter til å si noe.
 */
export function vekstEndring(serie: VekstPunkt[]): { fra: VekstPunkt; til: VekstPunkt; poeng: number } | null {
  if (serie.length < 2) return null;
  const fra = serie[0]!;
  const til = serie[serie.length - 1]!;
  return { fra, til, poeng: Math.round((til.gront - fra.gront) * 100) };
}
